import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { clearQueryCache } from './queryCache';
import { config } from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.userPoolWebClientId
});

// One-time migration: only a successful login on this version grants this marker.
// It controls the login screen, not API authorization (which remains in Cognito).
export const LOGIN_SESSION_VERSION = 'frontend-v2-2026-09-21';
const memoryVersions = new Map();
const versionKey = user => `QRBlant.${config.cognito.userPoolWebClientId}.${user.getUsername()}.loginVersion`;
const rememberLogin = user => {
  const key = versionKey(user);
  try { localStorage.setItem(key, LOGIN_SESSION_VERSION); }
  catch { memoryVersions.set(key, LOGIN_SESSION_VERSION); }
};
const forgetLogin = user => {
  const key = versionKey(user);
  memoryVersions.delete(key);
  try { localStorage.removeItem(key); } catch { /* Storage can be disabled. */ }
};
const currentUser = () => {
  const user = userPool.getCurrentUser();
  if (!user) return null;
  let version = memoryVersions.get(versionKey(user));
  try { version = localStorage.getItem(versionKey(user)) || version; } catch { /* Use in-memory storage. */ }
  if (version === LOGIN_SESSION_VERSION) return user;
  forgetLogin(user);
  user.signOut();
  clearQueryCache();
  return null;
};

export const authService = {
  signIn: (username, password) => {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password
      });

      user.authenticateUser(authDetails, {
        onSuccess: (result) => {
          rememberLogin(user);
          clearQueryCache();
          resolve({
            idToken: result.getIdToken().getJwtToken(),
            accessToken: result.getAccessToken().getJwtToken(),
            refreshToken: result.getRefreshToken().getToken()
          });
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  },

  signOut: () => {
    clearQueryCache();
    const user = userPool.getCurrentUser();
    if (user) {
      forgetLogin(user);
      user.signOut();
    }
  },

  getCurrentUser: () => {
    return new Promise((resolve, reject) => {
      const user = currentUser();

      if (!user) {
        resolve(null);
        return;
      }

      user.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        if (!session.isValid()) {
          resolve(null);
          return;
        }

        resolve({
          user,
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken()
        });
      });
    });
  },

  getIdToken: () => {
    return new Promise((resolve, reject) => {
      const user = currentUser();

      if (!user) {
        resolve(null);
        return;
      }

      user.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        resolve(session.getIdToken().getJwtToken());
      });
    });
  }
};
